'use client';

import { cn } from '../../lib/utils/cn';
import { Flex } from '../atoms/flex';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  ElementRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { SliderTooltip, type SliderTooltipProps } from './slider-tooltip';
import { useSliderContext } from './slider-context';
import { useSliderValues } from './hooks/use-slider-values';
import { getPercentage, getThumbInBoundsOffset } from './utils';

type SliderMarker = {
  value: number;
  label?: ReactNode;
  showMarker?: boolean;
};

export type SliderProps = ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  formatDisplayValue?: (value: number) => ReactNode;
  markers?: SliderMarker[];
  thumbSize?: number;
  classNames?: {
    wrapper?: string;
    tooltip?: SliderTooltipProps['classNames'];
    track?: string;
    range?: string;
    thumb?: string;
    marker?: string;
    markerValue?: string;
  };
};

const THUMB_SIZE = 26; //px

export const Slider = forwardRef<ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      disabled: disabledProp,
      onValueChange: onValueChangeProp,
      onValueCommit: onValueCommitProp,
      formatDisplayValue,
      markers,
      defaultValue,
      value: controlledValue,
      min: minProp = 0,
      max: maxProp = 100,
      step: stepProp = 1,
      minStepsBetweenThumbs: minStepsBetweenThumbsProp,
      classNames,
      thumbSize: thumbSizeProp,
      ...props
    },
    ref,
  ) => {
    // when rendered inside a <SliderField>, value/limits come from context,
    // otherwise the Slider manages its own state from props (standalone use)
    const context = useSliderContext();

    const [ownValues, handleOwnValueChange] = useSliderValues({
      value: controlledValue,
      defaultValue,
      onValueChange: onValueChangeProp,
    });

    const thumbValues = context?.values ?? ownValues;
    const onValueChange = context?.onValueChange ?? handleOwnValueChange;
    const onValueCommit = context?.onValueCommit ?? onValueCommitProp;
    const min = context?.min ?? minProp;
    const max = context?.max ?? maxProp;
    const step = context?.step ?? stepProp;
    const minStepsBetweenThumbs = context?.minStepsBetweenThumbs ?? minStepsBetweenThumbsProp;
    const disabled = context?.disabled ?? disabledProp;

    const isRange = thumbValues.length === 2;
    const inverted = props.inverted;

    const thumbSize = thumbSizeProp ?? THUMB_SIZE;

    const formatValue = useCallback(
      (value: number) => (formatDisplayValue ? formatDisplayValue(value) : String(value)),
      [formatDisplayValue],
    );

    const getPlacementPercent = useCallback(
      (value: number) =>
        getPercentage(value, {
          min,
          max,
        }),
      [min, max],
    );

    const hasMarkerLabels = useMemo(() => markers?.some((m) => m.label) ?? false, [markers]);

    const rangeTooltipPlacementPercent = useMemo(
      () =>
        getPlacementPercent(
          ((thumbValues[0] ?? 0) + (thumbValues[thumbValues.length - 1] ?? 0)) / 2,
        ),
      [thumbValues, getPlacementPercent],
    );

    const getMarkerPlacement = useCallback(
      (val: number) => {
        const rawPercent = getPlacementPercent(val);
        const percent = inverted ? 100 - rawPercent : rawPercent;
        const offset = getThumbInBoundsOffset(thumbSize, percent);
        return { percent, offset };
      },
      [getPlacementPercent, thumbSize, inverted],
    );

    const processedMarkers = useMemo(() => {
      const thumbMin = thumbValues[0] ?? 0;
      const thumbMax = thumbValues[thumbValues.length - 1] ?? 0;

      return markers?.map((marker) => {
        const { value } = marker;
        const { percent, offset } = getMarkerPlacement(value);
        const isActive = isRange
          ? value >= thumbMin && value <= (thumbMax ?? 0)
          : value <= thumbMin;

        return { ...marker, percent, offset, isActive };
      });
    }, [markers, thumbValues, isRange, getMarkerPlacement]);

    return (
      <Flex className={cn('relative w-full pt-8', hasMarkerLabels && 'pb-5', classNames?.wrapper)}>
        {isRange && (
          <span
            className="pointer-events-none absolute -top-2 -translate-x-1/2"
            style={{
              left: `calc(${rangeTooltipPlacementPercent}% + ${getThumbInBoundsOffset(thumbSize, rangeTooltipPlacementPercent)}px)`,
            }}>
            <SliderTooltip disabled={disabled} classNames={classNames?.tooltip}>
              {`${formatValue(thumbValues[0] ?? 0)} - ${formatValue(thumbValues[thumbValues.length - 1] ?? 0)}`}
            </SliderTooltip>
          </span>
        )}
        <SliderPrimitive.Root
          data-slot="slider"
          ref={ref}
          className={cn(
            'relative flex h-8 grow touch-none items-center select-none',
            disabled && 'cursor-not-allowed',
          )}
          disabled={disabled}
          onValueChange={onValueChange}
          onValueCommit={onValueCommit}
          value={thumbValues}
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={minStepsBetweenThumbs}
          {...props}>
          <SliderPrimitive.Track
            data-slot="slider-track"
            className={cn(
              'relative h-2 grow overflow-hidden rounded-full bg-space-15',
              classNames?.track,
            )}>
            <SliderPrimitive.Range
              data-slot="slider-range"
              className={cn(
                'absolute h-full bg-indigo-crush data-[disabled]:bg-indigo-crush-40',
                classNames?.range,
              )}
            />
          </SliderPrimitive.Track>
          {processedMarkers?.map(
            ({ label, percent, offset, isActive, showMarker = true }, index) => (
              <span
                key={`marker-${index}`}
                className={cn(
                  'pointer-events-none absolute top-1/2 -translate-y-1/2',
                  // markers at the limits should be aligned with the edge of the track
                  percent === 0
                    ? 'translate-x-0'
                    : percent === 100
                      ? '-translate-x-full'
                      : '-translate-x-1/2',
                )}
                style={{
                  left:
                    percent === 0 || percent === 100
                      ? `${percent}%`
                      : `calc(${percent}% + ${offset}px)`,
                }}>
                {showMarker && (
                  <div
                    data-disabled={disabled || undefined}
                    className={cn(
                      'h-3 w-3 rounded-full',
                      isActive ? 'bg-indigo-crush' : 'bg-space-15',
                      disabled && isActive && 'bg-indigo-crush-40',
                      classNames?.marker,
                    )}
                  />
                )}
                {label && (
                  <span
                    className={cn(
                      'absolute top-full left-1/2 mt-3 -translate-x-1/2 text-xs whitespace-nowrap text-space-60',
                      classNames?.markerValue,
                    )}>
                    {label}
                  </span>
                )}
              </span>
            ),
          )}
          {thumbValues.map((value, index) => (
            <SliderPrimitive.Thumb
              key={`thumb-${index}`}
              data-slot="slider-thumb"
              style={{
                width: thumbSize,
                height: thumbSize,
              }}
              className={cn(
                'relative block cursor-pointer rounded-full border-2 border-space-15 bg-white shadow-sm transition-[color,box-shadow] duration-150 outline-none',
                'before:absolute before:-inset-2 before:content-[""]',
                // Hover
                'hover:border-indigo-crush',
                // Focus
                'focus:ring-[3px] focus:ring-focus-ring',
                // Focus visible
                'focus-visible:ring-[3px] focus-visible:ring-focus-ring',
                'data-[disabled]:pointer-events-none data-[disabled]:border-space-15 data-[disabled]:bg-space-08 data-[disabled]:shadow-none',
                classNames?.thumb,
              )}>
              {!isRange && (
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-[10px] -translate-x-1/2">
                  <SliderTooltip disabled={disabled} classNames={classNames?.tooltip}>
                    {formatValue(value)}
                  </SliderTooltip>
                </span>
              )}
            </SliderPrimitive.Thumb>
          ))}
        </SliderPrimitive.Root>
      </Flex>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;
